<template>
  <div>
    <form novalidate @submit.prevent autocomplete="client-form">
      <!-- Customer Details -->
      <div class="idb-block">
        <div class="idb-block-title">
          <h2>
            Client Information
            <help-icon docPath="/administration/customer/details/" />
          </h2>
        </div>

        <div class="idb-block-content">
          <div
            class="alert alert-warning"
            v-if="customer.isItemActioned"
          >This Client has a pending action against it and cannot be edited</div>

          <div
            class="alert alert-warning"
            v-if="customer.status == customerStatuses.onHold"
          >This Client is currently on hold</div>

          <!-- Paygate Id -->
          <div
            class="form-group row"
            :class="{invalid: $v.customer.paygateId.$error}"
            v-if="status != 'Create' && (isSystemAdmin || isFinance || isImplementations)"
          >
            <label class="label-control col-md-3">Client Id</label>
            <div class="col-md-6">
              <input
                type="text"
                class="form-control"
                :readonly="status != 'Create'"
                :disabled="disabledDueToConnectedMerchant"
                v-model="$v.customer.paygateId.$model"
              />
              <validation-messages v-model="$v.customer.paygateId" name="Paygate Id">
                <small
                  class="form-text small"
                  v-if="!$v.customer.paygateId.guid"
                >This has to be a valid guid</small>
              </validation-messages>
              <small v-if="status === 'Create'">Leave this blank for it to be auto-generated</small>
            </div>
          </div>

          <!-- Migrated Date -->
          <div
            class="form-group row"
            :class="{invalid: $v.customer.paygateId.$error}"
            v-if="customer.migratedDate"
          >
            <label class="label-control col-md-3">Migrated Date</label>
            <div class="col-md-6">
              <input type="text" class="form-control" readonly v-model="migratedDate" />
            </div>
          </div>

          <div
            class="form-group row"
            v-if="isSystemAdmin || isImplementations"
            :class="{invalid: $v.customer.accountId.$error}"
          >
            <label class="label-control col-md-3">Account Id</label>
            <div class="col-md-6">
              <input
                type="number"
                class="form-control"
                :readonly="status != 'Create'"
                :disabled="disabledDueToConnectedMerchant"
                v-model.trim="$v.customer.accountId.$model"
                onkeydown="return event.keyCode !== 69"
              />
              <validation-messages v-model="$v.customer.accountId" name="Account Id"></validation-messages>
              <small v-if="status === 'Create'">Leave this blank for it to be auto-generated</small>
            </div>
          </div>
          <div v-if="status != 'Create'">
            <!-- Customer ID -->
            <div class="form-group row">
              <label class="label-control col-md-3">Client Code</label>
              <div class="col-md-4">
                <input
                  type="text"
                  class="form-control"
                  readonly
                  :disabled="disabledDueToConnectedMerchant"
                  v-model="$v.customer.customerCode.$model"
                />
              </div>
            </div>
          </div>

          <!-- Type -->
          <div
            class="form-group row"
            :class="{invalid: $v.customer.type.$error}"
            v-if="vueAppVersion >= 110"
          >
            <label class="label-control col-md-3" :class="{required}">Type</label>
            <div class="col-md-4">
              <select
                v-model="$v.customer.type.$model"
                :disabled="status != 'Create' || disabledDueToConnectedMerchant"
                class="form-control"
                @change="customerTypeChange"
              >
                <option
                  v-for="(type, index) in Object.values(types)"
                  :key="index"
                  :value="type"
                >{{ type }}</option>
              </select>

              <!-- Validation -->
              <validation-messages v-model="$v.customer.type" name="type"></validation-messages>
            </div>
          </div>

          <!-- Parent Customer Name -->
          <div
            class="form-group row"
            v-if="vueAppVersion >= 110 && $v.customer.type.$model == types.general && canManageChildCustomers"
          >
            <label class="label-control col-md-3">Parent Customer</label>
            <div class="col-md-6">
              <b-form-select
                v-model="$v.customer.parentCustomerId.$model"
                :options="parentCustomers"
                text-field="label"
                value-field="id"
                @change="setSunBasedOnParentCustomer"
              ></b-form-select>
              <!-- Validation -->
              <validation-messages v-model="$v.customer.parentCustomerId" name="Parent Client"></validation-messages>
            </div>
          </div>

          <!-- Name -->
          <div class="form-group row" :class="{invalid: $v.customer.name.$error}">
            <label class="label-control col-md-3 required">Name</label>
            <div class="col-md-6">
              <input
                id="name"
                type="text"
                class="form-control"
                :disabled="disabledDueToConnectedMerchant"
                v-model.trim="$v.customer.name.$model"
                :readonly="!canAlter && !canRequestForChange"
                autocomplete="client-name"
              />

              <!-- Validation -->
              <validation-messages v-model="$v.customer.name" name="name"></validation-messages>
            </div>
          </div>

          <!-- Legal Name -->
          <div
            class="form-group row"
            :class="{invalid: $v.customer.legalName.$error}"
            v-if="customer.type != types.connectedMerchant"
          >
            <label class="label-control col-md-3" :class="{required}">Legal Name</label>
            <div class="col-md-6">
              <input
                id="name"
                type="text"
                class="form-control"
                v-model.trim="$v.customer.legalName.$model"
                :readonly="!canAlter&& !canRequestForChange"
                autocomplete="client-legal-name"
              />

              <!-- Validation -->
              <validation-messages v-model="$v.customer.legalName" name="legal name"></validation-messages>
            </div>
          </div>

          <!-- Company Number -->
          <div class="form-group row" :class="{invalid: $v.customer.companyNumber.$error}">
            <label class="label-control col-md-3">Company Number</label>
            <div class="col-md-6">
              <input
                id="name"
                type="text"
                class="form-control"
                v-model.trim="$v.customer.companyNumber.$model"
                :readonly="!canAlter && !canRequestForChange"
              />

              <!-- Validation -->
              <validation-messages v-model="$v.customer.companyNumber" name="company number"></validation-messages>
            </div>
          </div>

          <!-- HubSpot Id -->
          <div class="form-group row" :class="{invalid: $v.customer.hubSpotId.$error}">
            <label class="label-control col-md-3">HubSpot Id</label>
            <div class="col-md-6">
              <input
                type="text"
                class="form-control"
                :disabled="disabledDueToConnectedMerchant"
                v-model="$v.customer.hubSpotId.$model"
                :readonly="!canAlter && !canRequestForChange"
              />
              <validation-messages v-model="$v.customer.hubSpotId" name="hubSpot Id"></validation-messages>
            </div>
            <div class="col-md-2" v-if="canAlter && (isSystemAdmin || isImplementations)">
              <button
                class="btn btn-outline-secondary"
                type="button"
                id="sync-btn"
                :disabled="isLoading || disabledDueToConnectedMerchant"
                @click="checkValidation() && syncHubspot()"
              >Sync Details From HubSpot</button>
            </div>
          </div>

          <!-- Postcode -->
          <div class="form-group row" :class="{invalid: $v.customer.postcode.$error}">
            <label class="label-control col-md-3" :class="{required}">Postcode</label>
            <div class="col-md-6">
              <input
                type="text"
                class="form-control"
                v-model="$v.customer.postcode.$model"
                :readonly="!canAlter && !canRequestForChange"
                autocomplete="client-postcode"
              />
              <validation-messages v-model="$v.customer.postcode"></validation-messages>
            </div>
            <div class="col-md-2">
              <button
                class="btn btn-info"
                @click="getAddress"
                type="button"
                :disabled="($v.customer.postcode.$model === '' || $v.customer.postcode.$model === null || $v.customer.postcode.$model === undefined) || (!canAlter && !canRequestForChange)"
              >Lookup Address</button>
            </div>
          </div>

          <transition name="fade">
            <div
              class="form-group row"
              v-if="foundAddresses.length > 0 && !this.addressLookupInProgress"
            >
              <label class="label-control col-md-3">Select an Address</label>
              <div class="col-md-6">
                <select
                  class="form-control"
                  aria-label="Please select an address"
                  v-model="selectedAddress"
                  @change="setAddress"
                  :readonly="!canAlter && !canRequestForChange"
                >
                  <option selected>Please select an address</option>
                  <option
                    v-for="foundAddress in foundAddresses"
                    :key="foundAddress.addressLine1"
                    :value="foundAddress"
                  >{{foundAddress.addressLine1}}, {{foundAddress.addressTown}}, {{foundAddress.addressCounty}}</option>
                </select>
              </div>
            </div>
          </transition>

          <!-- Address -->
          <div class="form-group row" :class="{invalid: $v.customer.address.$error}">
            <label class="label-control col-md-3" :class="{required}">Address</label>
            <div class="col-md-6">
              <input
                type="text"
                class="form-control"
                :disabled="disabledDueToConnectedMerchant"
                v-model="$v.customer.address.$model"
                :readonly="!canAlter && !canRequestForChange"
                autocomplete="client-address"
              />
              <validation-messages v-model="$v.customer.address" name="Address"></validation-messages>
            </div>
          </div>

          <!-- Address 2 -->
          <div class="form-group row" :class="{invalid: $v.customer.address2.$error}">
            <label class="label-control col-md-3">Address 2</label>
            <div class="col-md-6">
              <input
                type="text"
                class="form-control"
                :disabled="disabledDueToConnectedMerchant"
                v-model="$v.customer.address2.$model"
                :readonly="!canAlter && !canRequestForChange"
                autocomplete="client-address2"
              />
              <validation-messages v-model="$v.customer.address2" name="Address2"></validation-messages>
            </div>
          </div>

          <!-- Town -->
          <div class="form-group row" :class="{invalid: $v.customer.town.$error}">
            <label class="label-control col-md-3" :class="{required}">Town</label>
            <div class="col-md-6">
              <input
                type="text"
                class="form-control"
                :disabled="disabledDueToConnectedMerchant"
                v-model="$v.customer.town.$model"
                :readonly="!canAlter && !canRequestForChange"
                autocomplete="client-town"
              />
              <validation-messages v-model="$v.customer.town" name="Town"></validation-messages>
            </div>
          </div>

          <!-- Country -->
          <div class="form-group row" :class="{invalid: $v.customer.county.$error}">
            <label class="label-control col-md-3" :class="{required}">County</label>
            <div class="col-md-6">
              <input
                type="text"
                class="form-control"
                :disabled="disabledDueToConnectedMerchant"
                v-model="$v.customer.county.$model"
                :readonly="!canAlter && !canRequestForChange"
                autocomplete="client-country"
              />
              <validation-messages v-model="$v.customer.county" name="County"></validation-messages>
            </div>
          </div>

          <!-- Start Date -->
          <div class="form-group row" :class="{invalid: $v.customer.startDate.$error}">
            <label class="label-control col-md-3">Start Date</label>
            <div class="col-md-3">
              <div class="input-group date">
                <date-picker
                  v-model="$v.customer.startDate.$model"
                  :config="dateOptions"
                  :wrap="true"
                  :readonly="!canAlter"
                ></date-picker>
                <div class="input-group-addon">
                  <span class="far fa-calendar"></span>
                </div>
              </div>

              <!-- Validation -->
              <validation-messages v-model="$v.customer.startDate" name="start date"></validation-messages>
            </div>
          </div>

          <!-- Status -->
          <div class="form-group row" :class="{invalid: $v.customer.status.$error}">
            <label class="label-control col-md-3" :class="{required}">Status</label>
            <div class="col-md-5">
              <b-form-select
                v-model="$v.customer.status.$model"
                :options="statuses"
                :disabled="!canAlter"
                @change="changedCustomerStatus"
              ></b-form-select>
              <!-- Validation -->
              <validation-messages v-model="$v.customer.status"></validation-messages>
            </div>
          </div>

          <!-- End Date -->
          <div
            class="form-group row"
            :class="{invalid: $v.customer.endDate.$error}"
            v-show="$v.customer.status.$model === customerStatuses.inactive"
          >
            <label class="label-control col-md-3">End Date</label>
            <div class="col-md-3">
              <div class="input-group date">
                <date-picker
                  v-model="$v.customer.endDate.$model"
                  :config="dateOptions"
                  :wrap="true"
                  :readonly="!canAlter"
                ></date-picker>
                <div class="input-group-addon">
                  <span class="far fa-calendar"></span>
                </div>
              </div>

              <!-- Validation -->
              <validation-messages v-model="$v.customer.endDate" name="end date"></validation-messages>
            </div>
          </div>

          <!-- Leave Reason -->
          <div
            class="form-group row"
            :class="{invalid: $v.customer.reason.$error}"
            v-if="$v.customer.status.$model === customerStatuses.inactive"
          >
            <label class="label-control col-md-3" :class="{required}">Leave Reason</label>
            <div class="col-md-5">
              <b-form-select
                v-model="$v.customer.reason.$model"
                :options="customerLeaveReasons"
                :disabled="!canAlter"
              ></b-form-select>
              <!-- Validation -->
              <validation-messages v-model="$v.customer.reason"></validation-messages>
            </div>
          </div>

          <!-- Sector -->
          <div class="form-group row" :class="{invalid: $v.customer.sector.$error}">
            <label class="label-control col-md-3" :class="{required}">Sector</label>
            <div class="col-md-5">
              <vue-select
                v-model="$v.customer.sector.$model"
                :options="sectors"
                :closeOnSelect="true"
                :reduce="s => Number(s.code)"
                :disabled="!canAlter"
              ></vue-select>
              <!-- Validation -->
              <validation-messages v-model="$v.customer.sector"></validation-messages>
            </div>
          </div>

          <!-- SUN -->
          <div
            class="form-group row"
            :class="{invalid: $v.customer.sunId.$error}"
            v-if="canViewSun"
          >
            <label class="label-control col-md-3" :class="{required}">SUN</label>
            <div class="col-md-5">
              <vue-select
                v-if="canEditSun"
                v-model="$v.customer.sunId.$model"
                :options="suns"
                :closeOnSelect="true"
                label="label"
                :reduce="suns => suns.id"
                :clearable="false"
                :disabled="selectedParentCustomer.type === 'Connected Merchant'"
              ></vue-select>
              <input class="form-control" v-else v-model="selectedSun.label" readonly disabled />
              <!-- Validation -->
              <validation-messages v-model="$v.customer.sunId" name="SUN"></validation-messages>
            </div>
            <div class="col-md-2" v-if="canViewSun && this.customer.sunId">
              <button class="btn btn-outline-secondary" type="button" @click="viewSunDetails">View</button>
            </div>
          </div>

          <!-- VAT -->
          <div
            class="form-group row"
            :class="{invalid: $v.customer.vatId.$error}"
            v-if="isSystemAdmin || isFinance || isImplementations"
          >
            <label class="label-control col-md-3" :class="{required}">VAT</label>
            <div class="col-md-4">
              <b-form-select
                v-model="$v.customer.vatId.$model"
                :options="vats"
                text-field="label"
                value-field="id"
                :disabled="!canAlter"
              ></b-form-select>
              <!-- Validation -->
              <validation-messages v-model="$v.customer.vatId" name="VAT"></validation-messages>
            </div>
          </div>

          <div class="form-group row" v-if="isSystemAdmin && vueAppVersion >= 110">
            <label class="label-control col-md-3">Intercompany</label>
            <div class="col-md-2">
              <p-check
                class="p-switch p-fill"
                color="primary"
                v-model="$v.customer.intercompany.$model"
              ></p-check>
            </div>
          </div>

          <div
            class="form-group row"
            v-if="isSystemAdmin && customer.intercompany"
            :class="{invalid: $v.customer.transactionalNominalCode.$error}"
          >
            <label class="label-control col-md-3" :class="{required}">Transactional Nominal Code</label>
            <div class="col-md-5">
              <input
                type="text"
                class="form-control"
                minlength="2"
                maxlength="2"
                v-model="$v.customer.transactionalNominalCode.$model"
              />
              <validation-messages
                v-model="$v.customer.transactionalNominalCode"
                name="Transactional Nominal Code"
              ></validation-messages>
            </div>
          </div>

          <div
            class="form-group row"
            v-if="isSystemAdmin && customer.intercompany"
            :class="{invalid: $v.customer.professionalServicesNominalCode.$error}"
          >
            <label
              class="label-control col-md-3"
              :class="{required}"
            >Professional Services Nominal Code</label>
            <div class="col-md-5">
              <input
                type="text"
                class="form-control"
                minlength="2"
                maxlength="2"
                v-model="$v.customer.professionalServicesNominalCode.$model"
              />
              <validation-messages
                v-model="$v.customer.professionalServicesNominalCode"
                name="Professional Services Nominal Code"
              ></validation-messages>
            </div>
          </div>

          <!-- Description -->
          <div class="form-group row" :class="{invalid: $v.customer.description.$error}">
            <label class="label-control col-md-3">Description</label>
            <div class="col-md-6">
              <textarea
                id="description"
                class="form-control"
                rows="4"
                v-model.trim="$v.customer.description.$model"
                :disabled="disabledDueToConnectedMerchant"
                :readonly="!canAlter && !canRequestForChange"
              ></textarea>

              <!-- Validation -->
              <validation-messages v-model="$v.customer.description" name="description"></validation-messages>
            </div>
          </div>

          <!-- Telephone -->
          <div class="form-group row" :class="{invalid: $v.customer.telephone.$error}">
            <label class="label-control col-md-3" :class="{required}">Telephone</label>
            <div class="col-md-4">
              <input
                type="tel"
                class="form-control"
                v-model="$v.customer.telephone.$model"
                :disabled="disabledDueToConnectedMerchant"
                :readonly="!canAlter && !canRequestForChange"
                autocomplete="client-telephone"
              />

              <!-- Validation -->
              <validation-messages v-model="$v.customer.telephone" name="telephone">
                <small
                  class="form-text small"
                  v-if="!$v.customer.telephone.phoneNumber"
                >This is not a valid phone number</small>
              </validation-messages>
            </div>
          </div>

          <!-- Website -->
          <div class="form-group row" :class="{invalid: $v.customer.website.$error}">
            <label class="label-control col-md-3">Website</label>
            <div class="col-md-5">
              <input
                type="text"
                class="form-control"
                v-model="$v.customer.website.$model"
                :disabled="disabledDueToConnectedMerchant"
                :readonly="!canAlter  && !canRequestForChange"
              />

              <!-- Validation -->
              <validation-messages v-model="$v.customer.website" name="website"></validation-messages>
            </div>
          </div>

          <!-- Enabled -->
          <div
            class="form-group row"
            v-if="(isSystemAdmin || isImplementations) && status == 'Edit'"
          >
            <label class="label-control col-md-3">Client Enabled</label>
            <div class="col-md-2">
              <p-check
                class="p-switch p-fill"
                color="primary"
                :disabled="disabledDueToConnectedMerchant || !required"
                v-model="$v.customer.enabled.$model"
              ></p-check>
            </div>
          </div>

          <!-- Email Quarantined -->
          <div class="form-group row">
            <label class="label-control col-md-3">Email Quarantined</label>
            <div class="col-md-2" v-if="(isSystemAdmin || isImplementations)">
              <p-check
                class="p-switch p-fill"
                color="primary"
                v-model="$v.customer.emailQuarantined.$model"
                :disabled="disabledDueToConnectedMerchant"
                :readonly="!canAlter"
              ></p-check>
            </div>
            <div class="col-md-2" v-else>
              <tick v-model="customer.emailQuarantined"></tick>
            </div>
          </div>

          <!-- Allow Letters -->
          <div class="form-group row" v-if="false">
            <label class="label-control col-md-3">Allow Post</label>
            <div class="col-md-2" v-if="(isSystemAdmin || isImplementations)">
              <p-check
                class="p-switch p-fill"
                color="primary"
                v-model="$v.customer.allowLetters.$model"
                :disabled="disabledDueToConnectedMerchant"
                :readonly="!canAlter"
              ></p-check>
            </div>
            <div class="col-md-2" v-else>
              <tick v-model="customer.allowLetters"></tick>
            </div>
          </div>

          <!-- SendNotificationOfChange -->
          <div
            class="form-group row"
            v-if="(isSystemAdmin || isImplementations) && customer.type != types.connectedMerchant"
          >
            <label class="label-control col-md-3">Send Notification Of Change</label>
            <div class="col-md-2">
              <p-check
                class="p-switch p-fill"
                color="primary"
                :disabled="disabledDueToConnectedMerchant"
                v-model="$v.customer.sendNotificationOfChange.$model"
                :readonly="!canAlter"
              ></p-check>
            </div>
          </div>

          <div class="form-group row" v-if="(isSystemAdmin || isImplementations)">
            <label class="col-form-label col-md-3">Ensure Unique Collections</label>
            <div class="col-md-3">
              <p-check
                class="p-switch p-fill"
                color="primary"
                :disabled="!canAlter"
                v-model="$v.customer.ensureUniqueCollections.$model"
              ></p-check>
            </div>
          </div>

          <div class="form-group row" v-if="(isSystemAdmin || isImplementations)">
            <label class="col-form-label col-md-3">Prevent Collections/DDI</label>
            <div class="col-md-3">
              <p-check
                class="p-switch p-fill"
                color="primary"
                :disabled="!canAlter"
                v-model="$v.customer.preventCollections.$model"
              ></p-check>
            </div>
          </div>

          <!-- Send Pre Collection Notification -->
          <div
            class="form-group row"
            v-if="(isSystemAdmin || isImplementations) && customer.type != types.connectedMerchant && vueAppVersion >= 130"
          >
            <label class="label-control col-md-3">Send Pre-Collection Notification</label>
            <div class="col-md-2">
              <p-check
                class="p-switch p-fill"
                color="primary"
                :disabled="disabledDueToConnectedMerchant || !canAlter"
                v-model="$v.customer.sendPreCollectionNotification.$model"
                :readonly="!canAlter"
              ></p-check>
            </div>
          </div>

          <!-- Allow Gift Aid -->
          <div class="form-group row">
            <label class="label-control col-md-3">Allow Gift Aid</label>
            <div class="col-md-2">
              <p-check
                class="p-switch p-fill"
                color="primary"
                :disabled="disabledDueToConnectedMerchant || !canAlter"
                v-model="$v.customer.allowGiftAid.$model"
              ></p-check>
            </div>
          </div>

          <!-- Email Invoice -->
          <div class="form-group row">
            <label class="label-control col-md-3">Email Invoice/Credit Note</label>
            <div class="col-md-2">
              <p-check
                class="p-switch p-fill"
                color="primary"
                :disabled="disabledDueToConnectedMerchant || !canAlter"
                v-model="$v.customer.emailInvoice.$model"
              ></p-check>
            </div>
          </div>

          <!-- Require Email Verification -->
          <div class="form-group row" v-if="hasOnboarding">
            <label class="label-control col-md-3">Require Online Signup Email Verification</label>
            <div class="col-md-2">
              <p-check
                class="p-switch p-fill"
                color="primary"
                v-model="$v.customer.verifyOnboardingEmail.$model"
                :readonly="!canAlter"
              ></p-check>
            </div>
          </div>
        </div>
      </div>

      <div class="idb-block" v-if="canViewBankDetails || canRequestForChange">
        <div class="idb-block-title">
          <h2>Correspondence Information</h2>
        </div>
        <div class="idb-block-content">
          <!-- Correspondence Name -->
          <div class="form-group row" :class="{invalid: $v.customer.contactName.$error}">
            <label class="label-control col-md-3">Correspondence Name</label>
            <div class="col-md-5">
              <input
                type="text"
                class="form-control"
                v-model="$v.customer.contactName.$model"
                :disabled="disabledDueToConnectedMerchant"
                :readonly="!canAlter  && !canRequestForChange"
              />

              <!-- Validation -->
              <validation-messages v-model="$v.customer.contactName" name="Contact Name"></validation-messages>
            </div>
          </div>

          <!-- Correspondence Email -->
          <div class="form-group row" :class="{invalid: $v.customer.contactEmail.$error}">
            <label class="label-control col-md-3" :class="{required}">Correspondence Email</label>
            <div class="col-md-5">
              <input
                type="text"
                class="form-control"
                v-model="$v.customer.contactEmail.$model"
                :disabled="disabledDueToConnectedMerchant"
                :readonly="!canAlter  && !canRequestForChange"
                autocomplete="client-correspondence-email"
              />

              <!-- Validation -->
              <validation-messages v-model="$v.customer.contactEmail" name="contactEmail"></validation-messages>
            </div>
          </div>

          <!-- Contact Telephone -->
          <div class="form-group row" :class="{invalid: $v.customer.contactTelephone.$error}">
            <label class="label-control col-md-3" :class="{required}">Correspondence Telephone</label>
            <div class="col-md-4">
              <input
                type="tel"
                class="form-control"
                v-model="$v.customer.contactTelephone.$model"
                :disabled="disabledDueToConnectedMerchant"
                :readonly="!canAlter && !canRequestForChange"
                autocomplete="client-correspondence-telephone"
              />

              <!-- Validation -->
              <validation-messages v-model="$v.customer.contactTelephone" name="telephone">
                <small
                  class="form-text small"
                  v-if="!$v.customer.contactTelephone.phoneNumber"
                >This is not a valid phone number</small>
              </validation-messages>
            </div>
          </div>
        </div>
      </div>

      <div class="idb-block" v-if="canViewBankDetails || canRequestForChange">
        <div class="idb-block-title">
          <h2>Settlement Information</h2>
        </div>
        <div class="idb-block-content">
          <!-- Credit Bank Sort Code -->
          <div class="form-group row" :class="{ invalid: $v.customer.creditBankSortCode.$error }">
            <label class="label-control col-md-3" :class="{required}">Credit Bank Sort Code</label>
            <div class="col-md-3">
              <the-mask
                type="text"
                class="form-control"
                v-model="$v.customer.creditBankSortCode.$model"
                :mask="['##-##-##']"
                :guide="true"
                ref="sortCode"
                :readonly="!canAlter&& !canRequestForChange"
              />
              <!-- Validation -->
              <validation-messages
                v-model="$v.customer.creditBankSortCode"
                name="credit bank sort code"
              >
                <small
                  class="form-text small"
                  v-if="!$v.customer.creditBankSortCode.bankAccountValidation"
                >Credit Bank Sort Code must be valid</small>
              </validation-messages>
            </div>
            <div v-if="bankAccountCreditValidation != null">
              <div
                v-if="bankAccountCreditValidation.valid || bankAccountCreditValidation.errorCode === '1502'"
                class="col-md-1"
              >
                <span style="font-size: 200%; color: green" class="fa fa-check-circle"></span>
              </div>
              <div v-else class="col-md-1">
                <span
                  class="text-warning small text-nowrap"
                >{{bankAccountCreditValidation.errorText}}</span>
              </div>
            </div>
          </div>
          <!-- Credit Bank Account Number -->
          <div
            class="form-group row"
            :class="{ invalid: $v.customer.creditBankAccountNumber.$error }"
          >
            <label class="label-control col-md-3" :class="{required}">Credit Bank Account Number</label>
            <div class="col-md-3">
              <the-mask
                type="text"
                class="form-control"
                v-model="$v.customer.creditBankAccountNumber.$model"
                :mask="['########']"
                :guide="false"
                ref="accountNumber"
                :readonly="!canAlter&& !canRequestForChange"
              />
              <validation-messages
                v-model="$v.customer.creditBankAccountNumber"
                name="credit bank account number"
              >
                <small
                  class="form-text small"
                  v-if="!$v.customer.creditBankAccountNumber.bankAccountValidation"
                >Credit Bank Account Number must be valid</small>
              </validation-messages>
            </div>
            <div v-if="bankAccountCreditValidation != null">
              <div v-if="bankAccountCreditValidation.valid" class="col-md-1">
                <span style="font-size: 200%; color: green" class="fa fa-check-circle"></span>
              </div>
              <div v-else-if="bankAccountCreditValidation.errorCode === '1502'" class="col-md-1">
                <span
                  class="text-warning small text-nowrap"
                >{{bankAccountCreditValidation.errorText}}</span>
              </div>
            </div>
          </div>

          <!-- Credit Bank Account Name -->
          <div class="form-group row" :class="{invalid: $v.customer.creditBankAccountName.$error}">
            <label class="label-control col-md-3" :class="{required}">Credit Bank Account Name</label>
            <div class="col-md-4">
              <input
                id="name"
                type="text"
                class="form-control"
                v-model.trim="$v.customer.creditBankAccountName.$model"
                :readonly="!canAlter&& !canRequestForChange"
                autocomplete="client-credit-bank-name"
              />

              <!-- Validation -->
              <validation-messages
                v-model="$v.customer.creditBankAccountName"
                name="credit bank account name"
              ></validation-messages>
            </div>
          </div>
          <div class="form-group row">
            <div class="col-md-3">
              <button class="btn btn-primary" @click="copyToDebitDetails">Copy to Debit Bank Details</button>
            </div>
          </div>
          <hr />
          <!-- Debit Bank Sort Code -->
          <div class="form-group row" :class="{ invalid: $v.customer.debitBankSortCode.$error }">
            <label class="label-control col-md-3" :class="{required}">Debit Bank Sort Code</label>
            <div class="col-md-3">
              <the-mask
                type="text"
                class="form-control"
                v-model="$v.customer.debitBankSortCode.$model"
                :mask="['##-##-##']"
                :guide="true"
                ref="sortCode"
                :readonly="!canAlter&& !canRequestForChange"
              />
              <!-- Validation -->
              <validation-messages
                v-model="$v.customer.debitBankSortCode"
                name="debit bank sort code"
              >
                <small
                  class="form-text small"
                  v-if="!$v.customer.debitBankSortCode.bankAccountValidation"
                >Debit Bank Sort Code must be valid and support DDIs</small>
              </validation-messages>
            </div>
            <div v-if="bankAccountDebitValidation != null">
              <div
                v-if="bankAccountDebitValidation.valid || bankAccountDebitValidation.errorCode === '1502'"
                class="col-md-1"
              >
                <span style="font-size: 200%; color: green" class="fa fa-check-circle"></span>
              </div>
              <div v-else class="col-md-1">
                <span
                  class="text-warning small text-nowrap"
                >{{bankAccountDebitValidation.errorText}}</span>
              </div>
            </div>
          </div>
          <!-- Debit Bank Account Number -->
          <div
            class="form-group row"
            :class="{ invalid: $v.customer.debitBankAccountNumber.$error }"
          >
            <label class="label-control col-md-3" :class="{required}">Debit Bank Account Number</label>
            <div class="col-md-3">
              <the-mask
                type="text"
                class="form-control"
                v-model="$v.customer.debitBankAccountNumber.$model"
                :mask="['########']"
                :guide="false"
                ref="accountNumber"
                :readonly="!canAlter&& !canRequestForChange"
              />
              <validation-messages
                v-model="$v.customer.debitBankAccountNumber"
                name="debit bank account number"
              >
                <small
                  class="form-text small"
                  v-if="!$v.customer.debitBankAccountNumber.bankAccountValidation"
                >Debit Bank Account Number must be valid and support DDIs</small>
              </validation-messages>
            </div>
            <div v-if="bankAccountDebitValidation != null">
              <div v-if="bankAccountDebitValidation.valid" class="col-md-1">
                <span style="font-size: 200%; color: green" class="fa fa-check-circle"></span>
              </div>
              <div v-else-if="bankAccountDebitValidation.errorCode === '1502'" class="col-md-1">
                <span
                  class="text-warning small text-nowrap"
                >{{bankAccountDebitValidation.errorText}}</span>
              </div>
            </div>
          </div>

          <!-- Debit Bank Account Name -->
          <div class="form-group row" :class="{invalid: $v.customer.debitBankAccountName.$error}">
            <label class="label-control col-md-3" :class="{required}">Debit Bank Account Name</label>
            <div class="col-md-4">
              <input
                id="name"
                type="text"
                class="form-control"
                v-model.trim="$v.customer.debitBankAccountName.$model"
                :readonly="!canAlter&& !canRequestForChange"
                autocomplete="client-debit-bank-name"
              />

              <!-- Validation -->
              <validation-messages
                v-model="$v.customer.debitBankAccountName"
                name="debit bank account name"
              ></validation-messages>
            </div>
          </div>
          <div v-if="isSystemAdmin || isFinance">
            <hr />
            <!-- Settlement Type -->
            <div
              class="form-group row"
              v-if="selectedSun && selectedSun.type != '3rd Party SUN'"
              :class="{invalid: $v.customer.settlementType.$error}"
            >
              <label class="label-control col-md-3" :class="{required}">Settlement Type</label>
              <div class="col-md-4">
                <p-radio
                  class="p-default p-round"
                  value="Net"
                  v-model="$v.customer.settlementType.$model"
                  :disabled="disabledDueToConnectedMerchant || isFinance"
                  name="settlementType"
                  :readonly="!canAlter&& !canRequestForChange"
                  v-if="vueAppVersion >= 110"
                >Net Settlement</p-radio>
                <p-radio
                  class="p-default p-round"
                  value="Gross"
                  v-model="$v.customer.settlementType.$model"
                  :disabled="disabledDueToConnectedMerchant || customer.type == types.connectedMerchant || isFinance"
                  name="settlementType"
                  :readonly="!canAlter && !canRequestForChange"
                >Gross Settlement</p-radio>
                <!-- Validation -->
                <validation-messages v-model="$v.customer.settlementType" name="settlement type"></validation-messages>
              </div>
            </div>

            <!-- Settlement Type -->
            <div
              class="form-group row"
              v-if="selectedSun && selectedSun.type == '3rd Party SUN'"
              :class="{invalid: $v.customer.settlementType.$error}"
            >
              <label class="label-control col-md-3" :class="{required}">Settlement Type</label>
              <div class="col-md-4">
                <div class="form-control-plaintext">
                  <span style="margin-right:5px">Gross Settlement</span>
                </div>
              </div>
            </div>

            <!-- Gross Settlement Method -->
            <div
              class="form-group row"
              :class="{invalid: $v.customer.grossSettlementMethod.$error}"
            >
              <label class="label-control col-md-3" :class="{required}">Billing Method</label>
              <div class="col-md-4">
                <p-radio
                  class="p-default p-round"
                  value="Direct Debit"
                  v-model="$v.customer.grossSettlementMethod.$model"
                  :disabled="disabledDueToConnectedMerchant || isFinance"
                  :readonly="!canAlter && !canRequestForChange"
                  name="grossSettlementMethod"
                >Pay by Direct Debit</p-radio>
                <p-radio
                  class="p-default p-round"
                  value="Other"
                  v-model="$v.customer.grossSettlementMethod.$model"
                  :disabled="disabledDueToConnectedMerchant || isFinance"
                  :readonly="!canAlter && !canRequestForChange"
                  name="grossSettlementMethod"
                >Pay by Other Means</p-radio>
                <!-- Validation -->
                <validation-messages
                  v-model="$v.customer.grossSettlementMethod"
                  name="gross settlement method"
                ></validation-messages>
              </div>
            </div>

            <div
              class="form-group row"
              :class="{invalid: $v.customer.netdownsAllowed.$error}"
              v-if="isFinance"
            >
              <label class="label-control col-md-3">Netdowns Allowed</label>
              <div class="col-md-3">
                <p-check
                  class="p-switch p-fill"
                  color="primary"
                  v-model="$v.customer.netdownsAllowed.$model"
                  :readonly="!isFinance"
                  :disabled="!isFinance"
                ></p-check>
                <validation-messages v-model="$v.customer.netdownsAllowed" name="netdowns allowed"></validation-messages>
              </div>
            </div>

            <div class="form-group row" :class="{invalid: $v.customer.leadInDays.$error}">
              <label class="label-control col-md-3">Lead In days</label>
              <div class="col-md-3">
                <input
                  type="number"
                  class="form-control"
                  v-model="$v.customer.leadInDays.$model"
                  :readonly="!isFinance"
                  :disabled="!isFinance"
                />
                <span class="validation-messages" v-if="$v.customer.leadInDays.$model > 14">
                  <!-- maxLength -->
                  <small class="form-text small text-warning">Lead in days are greater than 2 weeks</small>
                </span>
                <validation-messages v-model="$v.customer.leadInDays" name="lead in days"></validation-messages>
              </div>
            </div>
            <div class="form-group row" :class="{invalid: $v.customer.leadOutDays.$error}">
              <label class="label-control col-md-3">Lead Out days</label>
              <div class="col-md-3">
                <input
                  type="number"
                  class="form-control"
                  v-model="$v.customer.leadOutDays.$model"
                  :readonly="!isFinance"
                  :disabled="!isFinance"
                />
                <span class="validation-messages" v-if="$v.customer.leadOutDays.$model > 14">
                  <!-- maxLength -->
                  <small class="form-text small text-warning">Lead out days are greater than 2 weeks</small>
                </span>
                <validation-messages v-model="$v.customer.leadOutDays" name="lead out days"></validation-messages>
              </div>
            </div>

            <div
              class="form-group row"
              :class="{invalid: $v.customer.holdingAmount.$error}"
              v-if="vueAppVersion >= 110  && isFinance && selectedSun.type != '3rd Party SUN'"
            >
              <label class="label-control col-md-3">Holding Amount</label>
              <div class="col-md-3">
                <input
                  type="number"
                  class="form-control"
                  v-model="$v.customer.holdingAmount.$model"
                  :readonly="!canAlter"
                  :disabled="isFinance"
                />
                <validation-messages v-model="$v.customer.holdingAmount" name="holding amount"></validation-messages>
              </div>
            </div>

            <hr />

            <!-- Customer To Invoice -->
            <div class="form-group row" :class="{invalid: $v.customer.customerToInvoice.$error}">
              <label class="label-control col-md-3">Client To Invoice</label>
              <div class="col-md-4">
                <b-form-select
                  v-model="$v.customer.customerToInvoice.$model"
                  :options="customersToDebitOrCredit"
                  text-field="label"
                  value-field="id"
                  :disabled="isFinance"
                ></b-form-select>
                <!-- Validation -->
                <validation-messages
                  v-model="$v.customer.customerToInvoice"
                  name="Customer to Invoice"
                ></validation-messages>
              </div>
            </div>

            <!-- Customer To Debit -->
            <div class="form-group row" :class="{invalid: $v.customer.customerToDebit.$error}">
              <label class="label-control col-md-3">Client To Debit</label>
              <div class="col-md-4">
                <b-form-select
                  v-model="$v.customer.customerToDebit.$model"
                  :options="customersToDebitOrCredit"
                  text-field="label"
                  value-field="id"
                  :disabled="isFinance"
                ></b-form-select>
                <!-- Validation -->
                <validation-messages
                  v-model="$v.customer.customerToDebit"
                  name="Customer to Invoice"
                >
                  <small
                    v-if="!$v.customer.customerToDebit.notAllowed"
                  >Customer to debit must be the same as the current customer or the same as the customer to invoice</small>
                </validation-messages>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="idb-block">
        <div class="idb-block-title">
          <h2>{{customerContactsTitle}} Contacts</h2>
        </div>
        <div class="idb-block-content">
          <div class="row" :class="{invalid: $v.customer.contacts.$error}">
            <div class="col-md-12">
              <vue-good-table
                ref="customerContacts"
                @on-row-click="onRowClick"
                :columns="contactColumns"
                :rows="customer.contacts"
                :totalRows="customer.contacts.length "
                :pagination-options="{
                    enabled: true,
                    perPage: 10,
                    dropdownAllowAll: false,
                    jumpFirstOrLast:true
                  }"
                styleClass="vgt-table striped bordered"
              >
                <div slot="table-actions">
                  <b-button
                    v-if="canAlter|| canRequestForChange"
                    @click.prevent="openAddContactModal"
                    class
                    variant="link"
                    v-b-popover.hover.top.d500="'Add a New Contact'"
                  >
                    <i class="fa fa-plus pointer dimmedIcon"></i>
                  </b-button>
                </div>
                <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'actions'">
                    <button
                      v-if="canAlter ||canRequestForChange"
                      class="btn btn-danger"
                      type="button"
                      @click.prevent="deleteContact(props.row,$event)"
                    >
                      <a class="fa fa-trash"></a>
                    </button>
                  </span>
                  <span v-else-if="props.column.field == 'primaryContact'">
                    <tick v-model="props.row.primaryContact"></tick>
                  </span>
                  <span v-else>{{props.formattedRow[props.column.field]}}</span>
                </template>
                <div slot="table-actions-bottom">
                  <div class="row" style="padding: 8px 0 8px 8px;">
                    <div class="col-md-2">
                      <button
                        class="btn btn-outline-primary"
                        @click.prevent="openAddContactModal"
                        v-if="canAlter || canRequestForChange"
                      >Add a New Contact</button>
                    </div>
                  </div>
                </div>
              </vue-good-table>
              <div class="alert alert-danger" v-if="$v.customer.contacts.$error">
                <small
                  class="form-text"
                  v-if="!$v.customer.contacts.min"
                >Please enter at least one Primary Contact</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="idb-block" v-if="((isFinance) && status === 'Edit' && vueAppVersion >= 110)">
        <div class="idb-block-title">
          <h2>Client Balance</h2>
        </div>
        <div class="idb-block-content">
          <div class="form-group row">
            <label class="label-control col-md-3">Current Balance</label>
            <label class="label-control col-md-3">
              <strong>£{{ parseFloat(currentLiability).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') }}</strong>
            </label>
          </div>
        </div>
      </div>

      <div class="idb-block" v-if="riskInformationAllowed && false">
        <div class="idb-block-title">
          <h2>Risk Information</h2>
        </div>
        <div class="idb-block-content">
          <!-- Number Of Collections Per Month -->
          <div
            class="form-group row"
            :class="{invalid: $v.customer.numberOfCollectionsPerMonth.$error}"
          >
            <label
              class="label-control col-md-3"
              :class="{required}"
            >Number Of Collections Per Month</label>
            <div class="col-md-3">
              <input
                type="numeric"
                class="form-control"
                min="0"
                step="1"
                v-model.trim="$v.customer.numberOfCollectionsPerMonth.$model"
                :disabled="disabledDueToConnectedMerchant"
                :readonly="this.customer.isItemActioned"
              />

              <!-- Validation -->
              <validation-messages
                v-model="$v.customer.numberOfCollectionsPerMonth"
                name="number of collections per month"
              ></validation-messages>
            </div>
          </div>

          <!-- Value Of Collections Per Month -->
          <div
            class="form-group row"
            :class="{invalid: $v.customer.valueOfCollectionsPerMonth.$error}"
          >
            <label class="label-control col-md-3" :class="{required}">Value Of Collections Per Month</label>
            <div class="col-md-3">
              <input
                type="numeric"
                class="form-control"
                min="0"
                step="1"
                v-model.trim="$v.customer.valueOfCollectionsPerMonth.$model"
                :disabled=" disabledDueToConnectedMerchant"
                :readonly="this.customer.isItemActioned"
              />

              <!-- Validation -->
              <validation-messages
                v-model="$v.customer.valueOfCollectionsPerMonth"
                name="value of collections per month"
              ></validation-messages>
            </div>
          </div>
        </div>
      </div>

      <div class="idb-block" v-if="secureLimitsAllowed && customer.type != types.connectedMerchant">
        <div class="idb-block-title">
          <h2>Secure Limits</h2>
        </div>
        <div class="idb-block-content">
          <div class="form-group row" :class="{invalid: $v.customer.secureLimitsId.$error}">
            <label class="label-control col-md-3">Secure Limits</label>
            <div class="col-md-5">
              <b-form-select
                v-model="$v.customer.secureLimitsId.$model"
                :options="secureLimits"
                text-field="name"
                value-field="id"
                @change="setSecureLimits"
              ></b-form-select>
              <!-- Validation -->
              <validation-messages v-model="$v.customer.secureLimitsId" name="Secure Limits"></validation-messages>
            </div>
          </div>

          <div v-if="$v.customer.secureLimitsId.$model">
            <!-- Max Amount Per Collection -->
            <div
              class="form-group row"
              :class="{invalid: $v.customer.maxAmountPerCollection.$error}"
            >
              <label class="label-control col-md-3">Max Amount Per Collection</label>
              <div class="col-md-3">
                <input
                  type="numeric"
                  class="form-control"
                  min="0"
                  step="1"
                  v-model.trim="$v.customer.maxAmountPerCollection.$model"
                  readonly="readonly"
                />

                <!-- Validation -->
                <validation-messages
                  v-model="$v.customer.maxAmountPerCollection"
                  name="max amount per collection"
                ></validation-messages>
              </div>
            </div>

            <!-- Max Amount Per Month -->
            <div
              class="form-group row"
              :class="{invalid: $v.customer.maxAmountPerMonth.$error, warn: !!customer.secureLimitsId && $v.customer.maxAmountPerMonth.$model < currentMonthlyAmount}"
            >
              <label class="label-control col-md-3">Max Amount Per Month</label>
              <div class="col-md-3">
                <input
                  type="numeric"
                  class="form-control"
                  min="0"
                  step="1"
                  v-model.trim="$v.customer.maxAmountPerMonth.$model"
                  readonly="readonly"
                />

                <!-- Validation -->
                <validation-messages
                  v-model="$v.customer.maxAmountPerMonth"
                  name="max Amount Per Month"
                ></validation-messages>
                <small
                  class="form-text small text-warning"
                  v-if="!!customer.secureLimitsId && $v.customer.maxAmountPerMonth.$model < currentMonthlyAmount"
                >Max Amount Per Month currently exceeds this value: {{formatCurrency(currentMonthlyAmount)}}</small>
              </div>
            </div>

            <!-- Max Collection Count Per Month -->
            <div
              class="form-group row"
              :class="{invalid: $v.customer.maxCollectionCountPerMonth.$error, warn: !!customer.secureLimitsId && $v.customer.maxCollectionCountPerMonth.$model < currentMonthlyCount}"
            >
              <label class="label-control col-md-3">Max Collection Count Per Month</label>
              <div class="col-md-3">
                <input
                  type="numeric"
                  class="form-control"
                  min="0"
                  step="1"
                  v-model.trim="$v.customer.maxCollectionCountPerMonth.$model"
                  readonly="readonly"
                />

                <!-- Validation -->
                <validation-messages
                  v-model="$v.customer.maxCollectionCountPerMonth"
                  name="max Collection Count Per Month"
                ></validation-messages>
                <small
                  class="form-text small text-warning"
                  v-if="!!customer.secureLimitsId && $v.customer.maxCollectionCountPerMonth.$model < currentMonthlyCount"
                >Max Collection Count Per Month currently exceeds this value: {{currentMonthlyCount}}</small>
              </div>
            </div>

            <!-- Max Collection Count Per Payer -->
            <div
              class="form-group row"
              :class="{invalid: $v.customer.maxCollectionCountPerPayer.$error}"
            >
              <label class="label-control col-md-3">Max Collection Count Per Customer</label>
              <div class="col-md-3">
                <input
                  type="numeric"
                  class="form-control"
                  min="0"
                  step="1"
                  v-model.trim="$v.customer.maxCollectionCountPerPayer.$model"
                  readonly="readonly"
                />

                <!-- Validation -->
                <validation-messages
                  v-model="$v.customer.maxCollectionCountPerPayer"
                  name="max Collection Count Per Customer"
                ></validation-messages>
              </div>
            </div>

            <!-- Max Refund Amount -->
            <!-- <div class="form-group row" :class="{invalid: $v.customer.maxRefundAmount.$error}">
              <label class="label-control col-md-3">Max Refund Amount</label>
              <div class="col-md-3">
                <input
                  type="numeric"
                  class="form-control"
                  min="0"
                  step="1"
                  v-model.trim="$v.customer.maxRefundAmount.$model"
                  readonly="readonly"
                />

                <validation-messages v-model="$v.customer.maxRefundAmount" name="Max Refund Amount"></validation-messages>
              </div>
            </div>-->

            <!-- <div
              class="form-group row"
              :class="{invalid: $v.customer.maxRefundAmountPerMonth.$error}"
            >
              <label class="label-control col-md-3">Max Refund Amount Per Month</label>
              <div class="col-md-3">
                <input
                  type="numeric"
                  class="form-control"
                  min="0"
                  step="1"
                  v-model.trim="$v.customer.maxRefundAmountPerMonth.$model"
                  readonly="readonly"
                />
                <validation-messages
                  v-model="$v.customer.maxRefundAmountPerMonth"
                  name="Max Refund Amount Per Month"
                ></validation-messages>
              </div>
            </div>-->
          </div>
        </div>
      </div>
      <div class="idb-block" v-if="notesAllowed && status === 'Edit'">
        <div class="idb-block-title">
          <h2>Client Notes</h2>
        </div>
        <div class="idb-block-content">
          <customer-notes :paygateId="this.selectedCustomer" :canLoad="true"></customer-notes>
        </div>
      </div>
      <div class="idb-block">
        <div class="idb-block-footer" v-if="canAlter || canRequestForChange || isFinance">
          <button
            class="btn btn-primary"
            type="submit"
            id="submit-btn"
            @click="checkValidation() && changeCustomer()"
            v-if="!canAlter && !isFinance"
          >Request Change</button>
          <button
            class="btn btn-primary"
            type="submit"
            id="submit-btn"
            @click="checkValidation() && saveCustomer()"
            v-if="canAlter || isFinance"
            :disabled="isLoading"
          >{{status === 'Edit'?'Save':status}}</button>

          <button
            id="delete-btn"
            v-if="canDeleteCustomer && status === 'Edit'"
            class="btn btn-danger pull-right"
            @click="deleteCustomer"
            type="button"
            :disabled="isLoading || disabledDueToConnectedMerchant"
          >Delete</button>
          <button
            id="cancel-btn"
            v-if="status === 'Create'"
            class="btn btn-danger pull-right"
            type="button"
            @click="back"
            :disabled="isLoading"
          >Cancel</button>
        </div>
      </div>
    </form>
    <contact-modal
      ref="customerContactModal"
      :type="contactModalType"
      :object="$v.customer.contacts"
      :require-address="false"
      :allowMultipleContactTypes="true"
      :contactTypes="['Operations','Finance', 'Technical', 'Marketing', 'Invoicing']"
      :allowPrimaryContact="true"
      @edit="editContact"
    ></contact-modal>
    <b-modal
      id="sunModal"
      ref="sunModal"
      variant="primary"
      no-close-on-backdrop
      no-close-on-esc
      ok-only
      hide-header-close
      centered
      :title="sunModal.alias"
      scrollable
      size="xl"
    >
      <div class="container-fluid">
        <div class="row">
          <div class="form-group col-6">
            <label class="label-control col-3">Alias:</label>
            <span class="form-control-static col-auto">{{sunModal.alias}}</span>
          </div>

          <div class="form-group col-6">
            <label class="label-control col-3">Number:</label>
            <span class="form-control-static col-auto">{{sunModal.number}}</span>
          </div>
        </div>

        <div class="row">
          <div class="form-group col-6">
            <label class="label-control col-3">Level:</label>
            <span class="form-control-static col-auto">{{sunModal.level}}</span>
          </div>

          <div class="form-group col-6">
            <label class="label-control col-3">Type:</label>
            <span class="form-control-static col-auto">{{sunModal.type}}</span>
          </div>
        </div>

        <div class="row">
          <div class="form-group col-6">
            <label class="label-control col-3">Service User Name:</label>
            <span class="form-control-static col-auto">{{sunModal.serviceUserName}}</span>
          </div>

          <div class="form-group col-6">
            <label class="label-control col-3">Advance Notice Period (In Days):</label>
            <span class="form-control-static col-auto">{{sunModal.advanceNoticeInDays}}</span>
          </div>
        </div>

        <div class="row">
          <div class="form-group col-6">
            <label class="label-control col-3">Direct Credits Allowed:</label>
            <tick v-model="sunModal.directCreditsAllowed"></tick>
          </div>

          <div class="form-group col-6">
            <label class="label-control col-3">Direct Debits Allowed:</label>
            <tick v-model="sunModal.directDebitsAllowed"></tick>
          </div>
        </div>

        <hr />

        <div class="row">
          <div class="form-group col-6">
            <label class="label-control col-3">Sort Code:</label>
            <span class="form-control-static col-auto">{{sunModal.sortCode}}</span>
          </div>

          <div class="form-group col-6">
            <label class="label-control col-3">Account Number:</label>
            <span class="form-control-static col-auto">{{sunModal.accountNumber}}</span>
          </div>
        </div>

        <div class="row">
          <div class="form-group col-6">
            <label class="label-control col-3">Bank Account Name:</label>
            <span class="form-control-static col-auto">{{sunModal.bankName}}</span>
          </div>

          <div class="form-group col-6">
            <label class="label-control col-3">Our Bank Reference:</label>
            <span class="form-control-static col-auto">{{sunModal.reference}}</span>
          </div>
        </div>
      </div>

      <template slot="modal-footer">
        <button type="button" class="btn btn-outline-primary mr-auto" @click="gotoSun">View SUN</button>
        <button type="button" class="btn btn-primary" @click="hideSunModal">Close</button>
      </template>
    </b-modal>
  </div>
</template>

<script>
// Third Party
import axios from 'axios'
import swal from 'sweetalert2'
import { mapGetters } from 'vuex'
import { TheMask } from 'vue-the-mask'
import _ from 'lodash'

// Mixins
import DataLeaveMixin from '@/Assets/Mixins/DataLeaveMixin'
import loading from '@/Assets/Mixins/LoadingMixin'
import ConnectedMerchantReadOnlyMixin from '@/Assets/Mixins/ConnectedMerchantReadOnlyMixin'

// Validations
import { phoneNumber, guid, exactLength } from '@/Assets/Validators'
import { required, requiredIf, url, maxLength, minLength, decimal, integer, minValue, email } from 'vuelidate/lib/validators'

// Helpers
import { formatDate } from '@/Assets/Helpers/DateHelper'

// Components
import ContactModal from '@/Components/Shared/Contact.vue'
import PrettyCheck from 'pretty-checkbox-vue/check'
import datePicker from '@/Assets/Components/DateTimePicker/DatePicker'
import VueSelect from 'vue-select'
import CustomerNotes from '@/Components/Platform/Customer/CustomerNotes'

// Constants
import roles from '@/Assets/Constants/roles'
import colours from '@/Assets/Constants/colours'
import customerTypes from '@/Assets/Constants/customerTypes'
import customerStatuses from '@/Assets/Constants/customerStatuses'
import customerSectors from '@/Assets/Constants/customerSectors'
import customerLeaveReasons from '@/Assets/Constants/customerLeaveReasons'

export default {
  name: 'CustomerEdit',
  mixins: [DataLeaveMixin, loading, ConnectedMerchantReadOnlyMixin],
  props: ['status'],
  components: {
    pCheck: PrettyCheck,
    ContactModal,
    datePicker,
    VueSelect,
    TheMask,
    CustomerNotes
  },
  computed: {
    selectedParentCustomer () {
      var result = this.parentCustomers.find(pc => pc.id === this.customer.parentCustomerId)
      return !result ? { type: '' } : result
    },
    customerContactsTitle () {
      if (this.customer.type === customerTypes.connectedMerchant) {
        return 'Connected Merchant'
      } else {
        return 'Client'
      }
    },
    canAlter () {
      return this.$store.getters.isInRoles([roles.LZAdmin, roles.Implementations]) && !this.customer.isItemActioned
    },
    notesAllowed () {
      return this.$store.getters.isInRoles([roles.LZAdmin, roles.Finance, roles.Implementations])
    },
    riskInformationAllowed () {
      return this.$store.getters.isInRoles([roles.Finance, roles.Implementations])
    },
    secureLimitsAllowed () {
      return this.$store.getters.isInRoles([roles.Finance, roles.Implementations])
    },
    canRequestForChange () {
      return this.$store.getters.isInRole(roles.ClientAdmin) && !this.customer.isItemActioned
    },
    canViewBankDetails () {
      return this.$store.getters.isInRoles([roles.ReadOnly, roles.Standard, roles.ClientAdmin, roles.LZAdmin, roles.Finance, roles.DevOps, roles.Qa, roles.Developer, roles.Implementations])
    },
    canViewSun () {
      return this.$store.getters.isInRoles([roles.LZAdmin, roles.Finance, roles.DevOps, roles.Qa, roles.Developer, roles.Implementations])
    },
    canEditSun () {
      if (this.isFinance || this.isSystemAdmin || this.isImplementations) {
        return true
      }

      return this.status !== 'Edit' || !this.customer.sunId || this.customer.status === 'Incomplete'
    },
    isImplementations () {
      return this.$store.getters.isInRole(roles.Implementations)
    },
    isFinance () {
      return this.$store.getters.isInRole(roles.Finance)
    },
    isSystemAdmin () {
      return this.$store.getters.isInRole(roles.LZAdmin)
    },
    canManageChildCustomers () {
      return this.isSystemAdmin
    },
    currencies () {
      return this.$store.state.currencies.currencies
    },
    canDeleteCustomer () {
      return this.$store.getters.isInRoles([roles.LZAdmin, roles.Implementations]) && this.$store.getters.selectedCustomer !== process.env.VUE_APP_LANDZ_ID
    },
    migratedDate () {
      return formatDate(this.customer.migratedDate, null, 'DD/MM/YYYY HH:mm')
    },
    types () {
      return customerTypes
    },
    statuses () {
      if (this.startingStatus === customerStatuses.incomplete || this.status === 'Create') {
        return Object.values(customerStatuses)
      }
      return Object.values(customerStatuses).filter(s => s !== customerStatuses.incomplete)
    },
    customerLeaveReasons () {
      return Object.values(customerLeaveReasons)
    },
    sectors () {
      return Object.keys(customerSectors).map(k => {
        return {
          code: k,
          label: customerSectors[k]
        }
      }).sort((a, b) => a.label.localeCompare(b.label))
    },
    selectedSun () {
      if (this.customer.sunId != null && this.suns.some(s => true)) {
        var sun = this.suns.find(s => s.id === this.customer.sunId)

        if (sun == null) {
          return { type: 'None' }
        }

        if (sun.type === '3rd Party SUN') {
          this.setSettlementToGross()
        }

        return sun
      }
      return { type: 'None' }
    },
    customersToDebitOrCredit () {
      var customers = JSON.parse(JSON.stringify(this.customers))
      customers.unshift({ id: null, label: '' })
      return customers
    },
    required () {
      return this.customer.status !== customerStatuses.incomplete
    },
    hasOnboarding () {
      return this.$store.getters.hasLicence('LicenceCollectionsOnBoarding') && this.$store.getters.isInRoles([roles.SystemUser, roles.LZAdmin, roles.Implementations]) && parseInt(process.env.VUE_APP_VERSION) >= 102
    },
    ...mapGetters(['selectedCustomer', 'customersOrdered', 'vueAppVersion'])
  },
  data () {
    return {
      customer: {
        paygateId: '',
        customerCode: '',
        name: null,
        description: '',
        telephone: '',
        website: '',
        defaultCurrency: 'GBP',
        emailQuarantined: false,
        allowLetters: false,
        allowLiveSubmissions: true,
        enabled: false,
        accountId: null,
        numberOfCollectionsPerMonth: 1,
        valueOfCollectionsPerMonth: 1,
        numberOfPayerSetupsPerMonth: 1,
        maxAmountPerCollection: null,
        maxAmountPerMonth: null,
        maxCollectionCountPerMonth: null,
        maxCollectionCountPerPayer: null,
        contacts: [],
        type: 'General',
        parentCustomerId: null,
        settlementType: 'Gross',
        sunId: null,
        notes: null,
        legalName: null,
        companyNumber: null,
        startDate: null,
        endDate: null,
        status: customerStatuses.incomplete,
        sector: '',
        creditBankSortCode: '',
        creditBankAccountNumber: '',
        creditBankAccountName: '',
        debitBankSortCode: '',
        debitBankAccountNumber: '',
        debitBankAccountName: '',
        leadOutDays: 2,
        leadInDays: 2,
        maxRefundAmount: null,
        maxRefundAmountPerMonth: null,
        grossSettlementMethod: 'Direct Debit',
        reason: null,
        hubSpotId: '',
        secureLimitsId: null,
        intercompany: false,
        transactionalNominalCode: null,
        professionalServicesNominalCode: null,
        sendPreCollectionNotification: false,
        holdingAmount: 0,
        netdownsAllowed: true,
        allowGiftAid: true,
        ensureUniqueCollections: false,
        preventCollections: false,
        vatId: null,
        customerToInvoice: null,
        customerToDebit: null,
        contactEmail: '',
        contactName: '',
        contactTelephone: '',
        address: null,
        address2: null,
        town: null,
        county: null,
        postcode: null,
        emailInvoice: null,
        verifyOnboardingEmail: false
      },
      contactColumns: [
        {
          label: 'Title',
          field: 'title'
        },
        {
          label: 'First Name',
          field: 'firstName'
        },
        {
          label: 'Last Name',
          field: 'lastName'
        },
        {
          label: 'Email',
          field: 'email'
        },
        {
          label: 'Phone',
          field: 'phone'
        },
        {
          label: 'Mobile',
          field: 'mobile'
        },
        {
          label: 'Job Title',
          field: 'jobTitle'
        },
        {
          label: 'Contact Type',
          field (rowObj) {
            return rowObj.contactType.join(', ')
          }
        },
        {
          label: 'Primary Contact',
          field: 'primaryContact'
        },
        {
          label: '',
          field: 'actions'
        },
        {
          field: 'notes',
          hidden: true
        },
        {
          field: 'address1',
          hidden: true
        },
        {
          field: 'address2',
          hidden: true
        }, {
          field: 'address3',
          hidden: true
        }, {
          field: 'address4',
          hidden: true
        }, {
          field: 'address5',
          hidden: true
        }

      ],
      contactModalType: 'Create',
      suns: [],
      bankAccounts: [],
      dateOptions: {
        format: 'DD/MM/YYYY',
        sideBySide: true
      },
      bankAccountCreditValidation: {},
      bankAccountDebitValidation: {},
      customerStatuses,
      secureLimits: [],
      sunModal: {
        title: null
      },
      parentCustomers: [],
      vats: [],
      customerToInvoice: null,
      customerToDebit: null,
      customers: [],
      tempPaygateId: null,
      foundAddresses: [],
      selectedAddress: null,
      currentMonthlyCount: 0,
      currentMonthlyAmount: 0,
      startingSunId: null,
      startingStatus: null,
      currentLiability: 0
    }
  },
  async mounted () {
    if (this.status === 'Edit') {
      await this.loadCustomer()
      this.loadCurrentLiability()
    }

    this.loadSuns()
    this.loadVat()
    this.loadSecureLimits()
    this.loadParentCustomers()
    try {
      this.customers = this.customersOrdered.map(co => ({ id: co.paygateId, label: co.name }))
    } catch { }
  },
  watch: {
    async selectedCustomer (newValue, oldValue) {
      if (this.status === 'Edit') {
        if (this.$v.$anyDirty && !this.$store.getters.forceLogout && this.tempPaygateId !== newValue) {
          const dirtyFields = []
          const form = this.$v.customer
          for (const field in form) {
            if (form.hasOwnProperty(field)) {
              if (form[field].$dirty) {
                dirtyFields.push(field)
              }
            }
          }

          console.log(dirtyFields)

          const result = await this.leaving()
          if (!result.isConfirmed) {
            this.tempPaygateId = oldValue
            this.$store.dispatch('setPaygateId', oldValue)
            return
          } else {
            this.tempPaygateId = null
          }
        }

        if (this.tempPaygateId !== newValue) {
          this.tempPaygateId = null
          await this.loadCustomer()
          this.loadCurrentLiability()
        }
      }
    },
    'customer.creditBankSortCode': function () {
      this.digitsOrDashes(this.$v.customer.creditBankSortCode, this.$v.customer.creditBankAccountNumber, 'bankAccountCreditValidation')
    },
    'customer.creditBankAccountNumber': function () {
      this.digitsOrDashes(this.$v.customer.creditBankSortCode, this.$v.customer.creditBankAccountNumber, 'bankAccountCreditValidation')
    },
    'customer.debitBankSortCode': function () {
      this.digitsOrDashes(this.$v.customer.debitBankSortCode, this.$v.customer.debitBankAccountNumber, 'bankAccountDebitValidation')
    },
    'customer.debitBankAccountNumber': function () {
      this.digitsOrDashes(this.$v.customer.debitBankSortCode, this.$v.customer.debitBankAccountNumber, 'bankAccountDebitValidation')
    }
  },
  methods: {
    async saveCustomer () {
      if (this.$v.customer.status.$model === 'Inactive' && this.$v.customer.status.$dirty) {
        var swalResult = await swal.fire({
          title: 'Set Client to Inactive?',
          html: `<div>This will suspend any Customers and Schedules for this Client</div>
          <div>Audit information will still be available</div>
          <div>If the intention is to permanently close this client, please use the "Cancel Schedules Or Customers" functionality in the Admin -> Collections menu.</div>
          <div class="mb-1"><strong>Are you REALLY sure you want to do this?</strong></div>
          <div>Please type <code>${this.customer.name}</code> to confirm </div>`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: colours.danger,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          input: 'text',
          inputValidator: result => new Promise((resolve, reject) => {
            result.toLowerCase() === this.customer.name.toLowerCase() ? resolve() : resolve(`You need to write "${this.customer.name}" to confirm`)
          })
        })
        if (!swalResult.isConfirmed) {
          return true
        }
      }
      try {
        const customer = { ...this.customer }
        customer.allowLiveSubmissions = true
        if (this.status === 'Create') {
          if (!customer.paygateId) {
            customer.paygateId = undefined
          }
          const response = await axios.post(`${process.env.VUE_APP_PLATFORM_API_URL}Customers`, customer, { showload: true, showerror: true, errormessage: 'Client failed to create' })
          customer.paygateId = response.data.id
          await this.$store.dispatch('addCustomerToList', customer)
          await this.$store.dispatch('setPaygateId', customer.paygateId)
          this.$toastr.s('New Client created', 'Created')
          this.$v.$reset()
          this.$router.push({ name: 'CustomerEdit', query: { paygateId: response.data.id } })
        } else {
          try {
            if (this.startingSunId !== this.customer.sunId && this.customer.status !== customerStatuses.incomplete) {
              try {
                // Get confirmation that they want to update the sun
                var swalResult = await swal.fire({
                  title: 'Change SUN',
                  text: 'You have updated this client\'s SUN.This will cause the direct debits for every customer to be cancelled and then re- lodged and collections cannot be made whilst this is in progress.Please confirm you want to continue?',
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonColor: colours.warning,
                  confirmButtonText: 'Yes',
                  cancelButtonText: 'No'
                })
              } catch (error) {
                // Do nothing as this will be if they clicked cancel
                return
              }

              if (!swalResult.isConfirmed) {
                return
              }
            }

            customer.startDate = new Date(customer.startDate)
            customer.startDate.setHours(0, 0, 0, 0)

            await axios.put(`${process.env.VUE_APP_PLATFORM_API_URL}Customers`, customer, { showload: true, showerror: true, errormessage: 'Client failed to save' })
            await this.$store.dispatch('updateCustomerInList', customer)
            this.$toastr.s('Client updated', 'Updated')
            await this.loadCustomer()
            this.$v.$reset()
          } catch { }
        }
      } catch { }
    },
    async changeCustomer () {
      const customer = { ...this.customer }
      customer.allowLiveSubmissions = !customer.allowLiveSubmissions
      const response = await axios.put(`${process.env.VUE_APP_PLATFORM_API_URL}Customers/Change`, customer, { showload: true, showerror: true, errormessage: 'Failed to request a change to the Client' })

      if (response.data.status === 'Updated') {
        this.$toastr.s('Client updated', 'Updated')
      } else {
        this.$toastr.s('Client Request Successful', 'Success')
      }
      await this.loadCustomer()
    },
    async deleteCustomer () {
      try {
        // Get confirmation from the user that they really want to delete the customer
        var swalResult = await swal.fire({
          title: 'Delete Client',
          html: `<div>This will permanently delete the Client and any relating data belonging to that Client</div>
          <div>Audit information will still be available</div>
          <div class="mb-1"><strong>Are you REALLY sure you want to do this?</strong></div>
          <div>Please type <code>${this.customer.name}</code> to confirm </div>`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: colours.danger,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          input: 'text',
          inputValidator: result => new Promise((resolve, reject) => {
            result.toLowerCase() === this.customer.name.toLowerCase() ? resolve() : resolve(`You need to write "${this.customer.name}" to confirm`)
          })
        })
      } catch (error) {
        // Do nothing as this will be if they clicked cancel
        return
      }

      if (!swalResult.isConfirmed) {
        return
      }

      try {
        await axios.delete(`${process.env.VUE_APP_PLATFORM_API_URL}Customers/${this.customer.paygateId}`, { showload: true, showerror: true, errormessage: 'Client failed to delete' })
        this.$v.$reset()
        await this.$store.dispatch('removeCustomerFromList', this.customer.paygateId)
        await this.$store.dispatch('checkPaygateId')
        this.$toastr.s('Client deleted', 'Deleted')

        this.$router.push('/')
      } catch { }
    },
    async loadCustomer () {
      try {
        this.$Progress.start()
        const promises = [axios.get(`${process.env.VUE_APP_PLATFORM_API_URL}customers/${this.selectedCustomer}`, { showerror: true, errormessage: 'Client failed to load' }), axios.get(`${process.env.VUE_APP_PLATFORM_API_URL}customers/contacts`, { showerror: true, errormessage: 'Client failed to load' })]
        var results = await Promise.all(promises)

        var response = results[0]
        var contactsResponse = results[1]

        response.data.contacts = contactsResponse.data.map(c => {
          return { town: null, county: null, ...c }
        })

        this.customer = response.data
        this.customer.allowLiveSubmissions = !this.customer.allowLiveSubmissions
        this.startingSunId = this.customer.sunId
        this.startingStatus = this.customer.status

        await this.validateNonDebounced(this.$v.customer.creditBankSortCode, this.$v.customer.creditBankAccountNumber, 'bankAccountCreditValidation')
        await this.validateNonDebounced(this.$v.customer.debitBankSortCode, this.$v.customer.debitBankAccountNumber, 'bankAccountDebitValidation')

        if (this.$store.getters.isInRoles([roles.SystemUser, roles.LZAdmin, roles.ClientAdmin, roles.Standard, roles.Finance, roles.Qa, roles.Developer, roles.Implementations])) {
          const currentValuesResponse = await axios.get(`${process.env.VUE_APP_DDMS_API_URL}securelimit/currentvalues`, {
            showload: true,
            showerror: true,
            errormessage: 'Failed to get current monthly collection count and amount'
          })
          this.currentMonthlyCount = currentValuesResponse.data.monthlyCount
          this.currentMonthlyAmount = currentValuesResponse.data.monthlyAmount
        }
      } catch {
        this.$Progress.fail()
      } finally {
        this.$v.$reset()
        this.$Progress.finish()
      }
    },
    async loadSuns () {
      try {
        var response = await axios.get(`${process.env.VUE_APP_PLATFORM_API_URL}sun/Dropdown`, { params: { onlyUsable: true }, showerror: true, errormessage: 'Failed to get SUNs' })
        this.suns = response.data
      } catch { }
    },
    async loadSecureLimits () {
      if (!this.isFinance && !this.isImplementations) {
        return
      }
      try {
        var response = await axios.get(`${process.env.VUE_APP_PLATFORM_API_URL}securelimits/Dropdown`, { showerror: true, errormessage: 'Failed to get Secure Limits' })
        this.secureLimits = response.data
        this.secureLimits.unshift({ id: null, name: '' })
      } catch { }
    },
    async loadParentCustomers () {
      if (!this.canManageChildCustomers) {
        return
      }

      try {
        var response = await axios.get(`${process.env.VUE_APP_PLATFORM_API_URL}ChildCustomer/Parents`, { showload: true, showerror: true, errormessage: 'Parent Clients failed to load' })
        this.parentCustomers = response.data
        this.parentCustomers.unshift({ label: '', id: undefined })
      } catch { }
    },
    setSecureLimits () {
      if (this.customer.secureLimitsId) {
        var secureLimit = this.secureLimits.find(sl => sl.id === this.customer.secureLimitsId)

        this.$v.customer.maxAmountPerCollection.$model = secureLimit.maxAmountPerCollection
        this.$v.customer.maxAmountPerMonth.$model = secureLimit.maxAmountPerMonth
        this.$v.customer.maxCollectionCountPerMonth.$model = secureLimit.maxCollectionCountPerMonth
        this.$v.customer.maxCollectionCountPerPayer.$model = secureLimit.maxCollectionCountPerPayer
        this.$v.customer.maxRefundAmount.$model = secureLimit.maxRefundAmount
        this.$v.customer.maxRefundAmountPerMonth.$model = secureLimit.maxRefundAmountPerMonth
      } else {
        this.$v.customer.maxAmountPerCollection.$model = null
        this.$v.customer.maxAmountPerMonth.$model = null
        this.$v.customer.maxCollectionCountPerMonth.$model = null
        this.$v.customer.maxCollectionCountPerPayer.$model = null
        this.$v.customer.maxRefundAmount.$model = null
        this.$v.customer.maxRefundAmountPerMonth.$model = null
      }
    },
    setSunBasedOnParentCustomer () {
      if (this.selectedParentCustomer.type === 'Connected Merchant') {
        this.customer.sunId = this.selectedParentCustomer.sunId
      } else {
        this.customer.sunId = null
      }
    },
    async openAddContactModal () {
      this.contactModalType = 'Create'
      this.$bvModal.show('contactModal')
    },
    onRowClick (params) {
      if (!this.canAlter && !this.canRequestForChange) {
        return
      }
      this.openAddContactModal()
      this.contactModalType = 'Edit'
      this.$refs.customerContactModal.populateContact(params.row)
    },
    async deleteContact (row, event) {
      event.stopPropagation()

      try {
        // Get confirmation from the user that they really want to delete the customer
        var swalResult = await swal.fire({
          title: 'Delete Contact',
          text: 'Are you sure you want to delete this contact?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: colours.danger,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        })
      } catch (error) {
        // Do nothing as this will be if they clicked cancel
        return
      }

      if (!swalResult.isConfirmed) {
        return
      }

      this.customer.contacts.splice(row.originalIndex, 1)
    },
    editContact (contact) {
      this.$set(this.customer.contacts, contact.originalIndex, contact)
      this.$v.customer.contacts.$touch()
    },
    digitsOrDashes: function (sortCode, accountNumber, validationProp) {
      if (sortCode.exactLength && accountNumber.exactLength) {
        this.validate(sortCode, accountNumber, validationProp)
      }
    },
    validate: _.debounce(async function (sortCode, accountNumber, validationProp) {
      if (!this.canViewBankDetails) {
        return
      }
      const data = { sortCode: sortCode.$model, accountNumber: accountNumber.$model }

      try {
        const response = await axios.post(`${process.env.VUE_APP_PLATFORM_API_URL}BankAccounts/ValidateBankAccount`, data, { showerror: true, errormessage: 'Failed to validate bank account' })

        this[validationProp] = response.data
      } catch { }
    }, 800),
    async validateNonDebounced (sortCode, accountNumber, validationProp) {
      if (!this.canViewBankDetails) {
        return
      }
      const data = { sortCode: sortCode.$model, accountNumber: accountNumber.$model }

      try {
        const response = await axios.post(`${process.env.VUE_APP_PLATFORM_API_URL}BankAccounts/ValidateBankAccount`, data, { showerror: true, errormessage: 'Failed to validate bank account' })

        this[validationProp] = response.data
      } catch { }
    },
    setSettlementToGross () {
      if (this.customer.type !== customerTypes.connectedMerchant) { this.customer.settlementType = 'Gross' }
    },
    async syncHubspot () {
      try {
        var response = await axios.get(`${process.env.VUE_APP_PLATFORM_API_URL}customers/Hubspot/${this.customer.hubSpotId}`, null, { showload: true, showerror: true, errormessage: 'HubSpot failed to Sync' })

        var hubspotResponse = response.data

        if (hubspotResponse.contacts.length > 0) {
          for (var i = 0; i < hubspotResponse.contacts.length; i++) {
            var hubspotContact = hubspotResponse.contacts[i]

            if (!this.customer.contacts.some(c => c.hubSpotId === hubspotContact.hubSpotId)) {
              this.customer.contacts.push(hubspotResponse.contacts[i])
            }
          }
          this.$v.customer.contacts.$touch()
        }

        this.customer.address = hubspotResponse.address.address
        this.customer.address2 = hubspotResponse.address.address2
        this.customer.city = hubspotResponse.address.city
        this.customer.id = hubspotResponse.address.id
        this.customer.postcode = hubspotResponse.address.postcode

        this.$toastr.s('Details Synced from HubSpot', 'Sync Successful')
      } catch { }
    },
    customerTypeChange () {
      if (this.customer.type === customerTypes.connectedMerchant) {
        this.customer.settlementType = 'Net'
      }
    },
    async loadCurrentLiability () {
      if ((this.isFinance) && this.status === 'Edit' && this.vueAppVersion >= 110) {
        try {
          const response = await axios.get(`${process.env.VUE_APP_PLATFORM_API_URL}Liability`, { showerror: true, errormessage: 'Failed to get current balance' })

          console.log(response)
          this.currentLiability = response.data.amount
        } catch { }
      }
    },
    async viewSunDetails () {
      const response = await axios.get(`${process.env.VUE_APP_PLATFORM_API_URL}sun/WithBankAccount/${this.customer.sunId}`, { showerror: true, errormessage: 'Failed to get SUN' })
      this.sunModal = response.data

      this.$bvModal.show('sunModal')
    },
    hideSunModal () {
      this.$bvModal.hide('sunModal')
    },
    gotoSun () {
      const routeData = this.$router.resolve({ name: 'SunEdit', params: { id: this.customer.sunId } })
      window.open(routeData.href, '_blank')
    },
    async changedCustomerStatus () {
      if (this.customer.status === customerStatuses.inactive) {
        this.customer.endDate = new Date()
      }

      if (this.customer.status === customerStatuses.incomplete) {
        this.customer.enabled = false
      }

      if (!this.customer.enabled && (this.customer.status === customerStatuses.new || this.customer.status === customerStatuses.active)) {
        try {
          // Get confirmation that they want to update the sun
          var swalResult = await swal.fire({
            title: 'Client not Enabled',
            text: 'You are about to set this client to an active status, but the client is not enabled, would you like to enable this client now?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: colours.warning,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
          })
        } catch (error) {
          // Do nothing as this will be if they clicked cancel
          return
        }

        if (swalResult.isConfirmed) {
          this.customer.enabled = true
        }
      }
    },
    async loadVat () {
      try {
        if (this.$store.getters.isInRoles([roles.LZAdmin, roles.Finance, roles.ClientAdmin, roles.Implementations])) {
          var response = await axios.get(`${process.env.VUE_APP_PLATFORM_API_URL}Vat/Dropdown`, { showload: true, showerror: true, errormessage: 'VAT failed to load' })
          this.vats = response.data

          if (this.status === 'Create') {
            this.customer.vatId = this.vats.find(v => v.label === 'Standard Rate - 20%').id
          }
        }
      } catch { }
    },
    async getAddress () {
      if (this.$v.customer.postcode.$invalid) {
        console.log('Postcode Invalid, aborting address lookup.')
        return
      }
      this.foundAddresses = []
      this.addressLookupInProgress = true
      try {
        const response = await axios.get(`${process.env.VUE_APP_PLATFORM_API_URL}addresslookup`, {
          params: { postcode: this.customer.postcode }
        })
        console.log(response)
        if (response.data.addresses.length === 1) {
          this.foundAddresses = response.data.addresses[0]
        } else {
          this.foundAddresses = response.data.addresses
        }
      } catch (e) {
        this.$toastr.w('Address Not Found', 'Sorry, we couldn\'t find that address')
      } finally {
        this.addressLookupInProgress = false
      }
    },
    setAddress () {
      if (this.selectedAddress != null) {
        this.customer.address = this.selectedAddress.addressLine1
        this.customer.address2 = this.selectedAddress.addressLine2
        this.customer.county = this.selectedAddress.addressCounty
        this.customer.town = this.selectedAddress.addressTown
      }
    },
    pad (num, size) {
      var s = '00' + num
      return s.substr(s.length - size)
    },
    formatCurrency (value) {
      var formatter = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      })
      return formatter.format(value)
    },
    copyToDebitDetails () {
      this.$v.customer.debitBankSortCode.$model = this.$v.customer.creditBankSortCode.$model
      this.$v.customer.debitBankAccountNumber.$model = this.$v.customer.creditBankAccountNumber.$model
      this.$v.customer.debitBankAccountName.$model = this.$v.customer.creditBankAccountName.$model
    }
  },
  validations () {
    if (this.customer.status === customerStatuses.incomplete) {
      return {
        customer: {
          paygateId: { guid },
          name: { required, maxLength: maxLength(50), minLength: minLength(6) },
          description: { maxLength: maxLength(200) },
          telephone: { phoneNumber },
          website: { url },
          contactName: {},
          contactEmail: { email },
          contactTelephone: { phoneNumber },
          defaultCurrency: {},
          customerCode: {},
          enabled: {},
          emailQuarantined: {},
          allowLetters: {},
          allowLiveSubmissions: {},
          accountId: { maxLength: maxLength(50), integer },
          numberOfCollectionsPerMonth: { integer, minValue: minValue(0) },
          valueOfCollectionsPerMonth: { numeric: decimal },
          numberOfPayerSetupsPerMonth: { integer, minValue: minValue(0) },
          maxAmountPerCollection: {},
          maxAmountPerMonth: {},
          maxCollectionCountPerMonth: {},
          maxCollectionCountPerPayer: {},
          type: {},
          settlementType: {},
          sunId: {},
          notes: {},
          legalName: {},
          companyNumber: {},
          startDate: {},
          endDate: {},
          status: {},
          sector: {},
          creditBankSortCode: {
            exactLength: exactLength(6)
          },
          creditBankAccountNumber: {
            exactLength: exactLength(8)
          },
          creditBankAccountName: {},
          debitBankSortCode: {
            exactLength: exactLength(6)
          },
          debitBankAccountNumber: {
            exactLength: exactLength(8)
          },
          debitBankAccountName: {},
          leadInDays: { minValue: minValue(0), integer },
          leadOutDays: { minValue: minValue(0), integer },
          maxRefundAmount: {},
          maxRefundAmountPerMonth: {},
          grossSettlementMethod: {

          },
          contacts: {
          },
          sendNotificationOfChange: {},
          reason: {},
          hubSpotId: {},
          secureLimitsId: {},
          intercompany: {},
          transactionalNominalCode: { exactLength: exactLength(2) },
          professionalServicesNominalCode: { exactLength: exactLength(2) },
          parentCustomerId: {},
          sendPreCollectionNotification: {},
          holdingAmount: { numeric: decimal },
          netdownsAllowed: {},
          allowGiftAid: {},
          ensureUniqueCollections: {},
          preventCollections: {},
          vatId: {},
          customerToInvoice: {
          },
          customerToDebit: {
            notAllowed: (value, model) => {
              if (value == null) {
                return true
              }

              if (value !== model.paygateId && value !== model.customerToInvoice) {
                return false
              }
              return true
            }
          },
          address: {},
          address2: {},
          town: {},
          county: {},
          postcode: {},
          emailInvoice: {},
          verifyOnboardingEmail: {}
        }
      }
    }

    return {
      customer: {
        paygateId: { guid },
        name: { required, maxLength: maxLength(50), minLength: minLength(6) },
        description: { maxLength: maxLength(200) },
        telephone: { required, phoneNumber },
        website: { url },
        contactName: {},
        contactEmail: { required, email },
        contactTelephone: { required, phoneNumber },
        defaultCurrency: {},
        customerCode: {},
        enabled: {},
        emailQuarantined: {},
        allowLetters: {},
        allowLiveSubmissions: {},
        accountId: { maxLength: maxLength(50), integer },
        numberOfCollectionsPerMonth: { required: required, integer, minValue: minValue(0) },
        valueOfCollectionsPerMonth: { required: required, numeric: decimal },
        numberOfPayerSetupsPerMonth: {},
        maxAmountPerCollection: {},
        maxAmountPerMonth: {},
        maxCollectionCountPerMonth: {},
        maxCollectionCountPerPayer: {},
        type: { required },
        settlementType: { required },
        sunId: { required },
        notes: {},
        legalName: { required: requiredIf((model) => model.type !== customerTypes.connectedMerchant) },
        companyNumber: {},
        startDate: {},
        endDate: {},
        status: { required },
        sector: { required },
        creditBankSortCode: {
          required,
          exactLength: exactLength(6),
          bankAccountValidation: () => {
            return !!this.bankAccountCreditValidation.valid
          }
        },
        creditBankAccountNumber: {
          required,
          exactLength: exactLength(8),
          bankAccountValidation: () => {
            return !!this.bankAccountCreditValidation.valid
          }
        },
        creditBankAccountName: { required },
        debitBankSortCode: {
          required,
          exactLength: exactLength(6),
          bankAccountValidation: () => {
            return !!(this.bankAccountDebitValidation.valid && this.bankAccountDebitValidation.supportsDDI)
          }
        },
        debitBankAccountNumber: {
          required,
          exactLength: exactLength(8),
          bankAccountValidation: () => {
            return !!(this.bankAccountDebitValidation.valid && this.bankAccountDebitValidation.supportsDDI)
          }
        },
        debitBankAccountName: { required },
        leadInDays: { minValue: minValue(0), integer },
        leadOutDays: { minValue: minValue(0), integer },
        maxRefundAmount: {},
        maxRefundAmountPerMonth: {},
        grossSettlementMethod: {
          required
        },
        contacts: {
          min: (model) => {
            return model.filter(c => c.primaryContact).length >= 1
          }
        },
        sendNotificationOfChange: {},
        reason: { required: requiredIf((model) => model.status === customerStatuses.inactive) },
        hubSpotId: {},
        secureLimitsId: {},
        intercompany: {},
        transactionalNominalCode: { required: requiredIf((model) => model.intercompany), exactLength: exactLength(2) },
        professionalServicesNominalCode: { required: requiredIf((model) => model.intercompany), exactLength: exactLength(2) },
        parentCustomerId: {},
        sendPreCollectionNotification: {},
        holdingAmount: { numeric: decimal },
        netdownsAllowed: {},
        allowGiftAid: {},
        verifyOnboardingEmail: {},
        ensureUniqueCollections: {},
        preventCollections: {},
        vatId: { required },
        customerToInvoice: {
        },
        customerToDebit: {
          notAllowed: (value, model) => {
            if (value == null) {
              return true
            }

            if (value !== model.paygateId && value !== model.customerToInvoice) {
              return false
            }
            return true
          }
        },
        address: { required },
        address2: {},
        town: { required },
        county: { required },
        postcode: { required },
        emailInvoice: {}
      }
    }
  }
}

</script>
